import React, { useState, useEffect, useCallback } from "react";
import { workQueueAPI, patientAPI, normalizePatient } from "../utils/api";
import UploadReport from "../components/UploadReport";
import { useApp } from "../context/AppContext";

export default function WorkQueue() {
  const { user } = useApp();
  const [data,           setData]           = useState({ pending: [], completed: [], registered_today: 0, total_pending: 0, total_completed: 0 });
  const [loading,        setLoading]        = useState(true);
  const getLocalDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };
  const [selectedDate, setSelectedDate] = useState(getLocalDate);
  const [uploadPatient,  setUploadPatient]  = useState(null);
  const [loadingPatient, setLoadingPatient] = useState(null);
  const [search,         setSearch]         = useState("");
  const [activeTab,      setActiveTab]      = useState("pending");

  const load = useCallback((date) => {
    setLoading(true);
    workQueueAPI.get(date)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(selectedDate);
    const interval = setInterval(() => {
      const today = getLocalDate();
      if (today !== selectedDate) setSelectedDate(today); // auto-advance date at midnight
      else load(selectedDate);
    }, 60000);
    return () => clearInterval(interval);
  }, [selectedDate, load]);

  const openUpload = async (patientId) => {
    setLoadingPatient(patientId);
    try {
      const p = await patientAPI.get(patientId);
      setUploadPatient(normalizePatient(p));
    } catch {
      alert("Failed to load patient data");
    } finally {
      setLoadingPatient(null);
    }
  };

  const handleUploadDone = () => {
    setUploadPatient(null);
    load(selectedDate);
  };

  const filterPatients = (list) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(p =>
      p.name?.toLowerCase().includes(q) ||
      p.id?.toLowerCase().includes(q) ||
      p.doctor?.toLowerCase().includes(q)
    );
  };

  // registered stores BD time as "2026-08-04 18:30:00" (no T, no Z) for new patients
  // or "2026-08-04" (date-only) for old patients.
  // Parse as BD time — treat the string as local BD time directly.
  const parseBDTime = (str) => {
    if (!str) return null;
    // Already ISO with T — old format or reports.created_at
    if (str.includes("T")) return new Date(str);
    // "2026-08-04 18:30:00" — parse as BD local time
    if (str.length >= 16) return new Date(str.replace(" ", "T") + "+06:00");
    // "2026-08-04" — date only, no time available
    return new Date(str + "T00:00:00+06:00");
  };

  const formatTime = (str) => {
    if (!str) return "—";
    try {
      const d = parseBDTime(str);
      if (!d || isNaN(d)) return "—";
      // If date-only (no time component), show just the date
      if (str.length === 10) return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Dhaka" });
    } catch { return str; }
  };

  const timeAgo = (str) => {
    if (!str) return "";
    try {
      const d = parseBDTime(str);
      if (!d || isNaN(d)) return "";
      const diff = Math.floor((Date.now() - d.getTime()) / 60000);
      if (diff < 1)  return "Just now";
      if (diff < 60) return `${diff} min${diff !== 1 ? "s" : ""} ago`;
      const h = Math.floor(diff / 60);
      if (h < 24) return `${h} hour${h !== 1 ? "s" : ""} ago`;
      const days = Math.floor(h / 24);
      return `${days} day${days !== 1 ? "s" : ""} ago`;
    } catch { return ""; }
  };

  const getInitials = (name) => (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  const AVATAR_COLORS = [
    { bg: "#EEF2FF", color: "#4F46E5" },
    { bg: "#FEF3C7", color: "#92400E" },
    { bg: "#DCFCE7", color: "#166534" },
    { bg: "#FCE7F3", color: "#9D174D" },
    { bg: "#E0F2FE", color: "#0369A1" },
    { bg: "#FEF9C3", color: "#713F12" },
  ];
  const avatarColor = (id) => AVATAR_COLORS[(id || "").charCodeAt(id?.length - 1) % AVATAR_COLORS.length];

  const pendingFiltered   = filterPatients(data.pending   || []);
  const completedFiltered = filterPatients(data.completed || []);
  const activeList        = activeTab === "pending" ? pendingFiltered : completedFiltered;
  const isPendingTab      = activeTab === "pending";

  // ── Stat cards ─────────────────────────────────────────────────────────────
  const stats = [
    {
      label: "Total pending",
      value: data.total_pending || 0,
      icon:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
      bg:    "#FEF3C7",
      color: "#D97706",
      labelColor: "#B45309",
      borderColor: "#F59E0B",
    },
    {
      label: "Registered today",
      value: data.registered_today || 0,
      icon:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="1.8" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
      bg:    "#EFF6FF",
      color: "#1D4ED8",
      labelColor: "var(--text-secondary)",
      borderColor: "transparent",
    },
    {
      label: "Reports today",
      value: data.total_completed || 0,
      icon:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="1.8" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
      bg:    "#DCFCE7",
      color: "#059669",
      labelColor: "#065F46",
      borderColor: "#10B981",
    },
  ];

  // ── Patient row ─────────────────────────────────────────────────────────────
  const PatientRow = ({ p }) => {
    const ac = avatarColor(p.id);
    const isLoading = loadingPatient === p.id;
    return (
      <div style={{
        display: "grid",
        gridTemplateColumns: "2fr 1.1fr 1.3fr 1.1fr auto",
        alignItems: "center",
        padding: "13px 20px",
        borderBottom: "0.5px solid var(--border, #E2E8F0)",
        gap: 0,
        transition: "background 0.12s",
        cursor: "default",
      }}
        onMouseEnter={e => e.currentTarget.style.background = "var(--surface-1, #F8FAFC)"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >
        {/* Patient info */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
            background: ac.bg, color: ac.color,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 500,
          }}>
            {getInitials(p.name)}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)" }}>{p.name}</div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2, display: "flex", gap: 6 }}>
              <span style={{ background: "var(--surface-1,#F1F5F9)", borderRadius: 4, padding: "1px 5px", fontWeight: 500, fontSize: 11 }}>{p.id}</span>
              {p.age && <span>{p.age}Y · {p.sex || "—"}</span>}
              {p.blood && <span style={{ color: "#EF4444", fontWeight: 500 }}>{p.blood}</span>}
            </div>
          </div>
        </div>

        {/* Registered time */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{formatTime(p.registered)}</div>
          <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>{timeAgo(p.registered)}</div>
        </div>

        {/* Referred by */}
        <div>
          {p.doctor ? (
            <>
              <div style={{ fontSize: 13, color: "var(--text-primary)" }}>Dr. {p.doctor}</div>
            </>
          ) : (
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>—</span>
          )}
          {isPendingTab && p.notes && (
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 3, fontStyle:"italic" }}>{p.notes}</div>
          )}
        </div>

        {/* Status */}
        <div>
          {isPendingTab ? (
            <div>
              {p.pending_tests && p.pending_tests.length > 0 ? (
                <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                  {(p.uploaded_tests || []).map(t => (
                    <span key={t} style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, color:"#166534" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                      {t}
                    </span>
                  ))}
                  {p.pending_tests.map(t => (
                    <span key={t} style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, color:"#92400E" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      {t}
                    </span>
                  ))}
                </div>
              ) : (
                <span style={{
                  display:"inline-flex", alignItems:"center", gap:5,
                  padding:"4px 10px", borderRadius:6, fontSize:12, fontWeight:500,
                  background:"#FEF3C7", color:"#92400E", border:"0.5px solid #FCD34D",
                }}>
                  <span style={{ width:6, height:6, borderRadius:"50%", background:"#F59E0B", display:"inline-block" }} />
                  Pending upload
                </span>
              )}
            </div>
          ) : (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500,
              background: "#DCFCE7", color: "#166534", border: "0.5px solid #86EFAC",
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#10B981", display: "inline-block" }} />
              {p.report_count} report{p.report_count !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Action */}
        <div>
          {isPendingTab ? (
            <button
              onClick={() => openUpload(p.id)}
              disabled={isLoading}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "7px 14px",
                background: isLoading ? "var(--surface-1,#F1F5F9)" : "var(--fill-accent,#2563EB)",
                color: isLoading ? "var(--text-secondary)" : "#fff",
                border: "none", borderRadius: "var(--radius,8px)",
                fontSize: 13, fontWeight: 500,
                cursor: isLoading ? "not-allowed" : "pointer",
                whiteSpace: "nowrap",
              }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>
              {isLoading ? "Loading…" : "Upload"}
            </button>
          ) : (
            <button
              onClick={() => openUpload(p.id)}
              disabled={isLoading}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "7px 14px",
                background: "var(--surface-2,#fff)",
                color: "var(--text-secondary)",
                border: "0.5px solid var(--border-strong,#CBD5E1)",
                borderRadius: "var(--radius,8px)",
                fontSize: 13, fontWeight: 500,
                cursor: "pointer", whiteSpace: "nowrap",
              }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add more
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: "28px 32px", minHeight: "100vh", background: "var(--surface-0, #F8FAFC)" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0, color: "var(--text-primary)" }}>Work queue</h1>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 4 }}>
            Today's registered patients — upload reports directly from here.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 14px", border: "0.5px solid var(--border-strong,#CBD5E1)",
            borderRadius: "var(--radius,8px)", background: "var(--surface-2,#fff)",
            fontSize: 13, color: "var(--text-primary)",
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" style={{ color: "var(--text-secondary)", flexShrink: 0 }}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              style={{
                border: "none", background: "transparent", fontSize: 13,
                fontFamily: "inherit", color: "var(--text-primary)", outline: "none", cursor: "pointer",
              }}
            />
          </div>
          <button
            onClick={() => load(selectedDate)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 14px", border: "0.5px solid var(--border-strong,#CBD5E1)",
              borderRadius: "var(--radius,8px)", background: "var(--surface-2,#fff)",
              fontSize: 13, fontWeight: 500, cursor: "pointer", color: "var(--text-primary)",
            }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
        {stats.map(s => (
          <div key={s.label} style={{
            background: "var(--surface-2,#fff)",
            border: "0.5px solid var(--border,#E2E8F0)",
            borderLeft: s.borderColor !== "transparent" ? `3px solid ${s.borderColor}` : "0.5px solid var(--border,#E2E8F0)",
            borderRadius: s.borderColor !== "transparent" ? "0 12px 12px 0" : 12,
            padding: "18px 20px",
            display: "flex", alignItems: "center", gap: 14,
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, flexShrink: 0,
              background: s.bg,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {s.icon}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: s.labelColor, marginBottom: 4 }}>
                {s.label}
              </div>
              <div style={{ fontSize: 28, fontWeight: 500, color: s.color, lineHeight: 1 }}>
                {s.value}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar — search + tabs */}
      <div style={{
        background: "var(--surface-2,#fff)",
        border: "0.5px solid var(--border,#E2E8F0)",
        borderRadius: 12, padding: "12px 16px",
        marginBottom: 16,
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <div style={{
          flex: 1, display: "flex", alignItems: "center", gap: 8,
          background: "var(--surface-1,#F8FAFC)",
          border: "0.5px solid var(--border,#E2E8F0)",
          borderRadius: "var(--radius,8px)", padding: "8px 12px",
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" style={{ color: "var(--text-muted)", flexShrink: 0 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            type="text"
            placeholder="Search by name, EMR ID, or doctor..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              border: "none", background: "transparent", fontSize: 13,
              fontFamily: "inherit", color: "var(--text-primary)", outline: "none", width: "100%",
            }}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, lineHeight: 1 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true" style={{ color: "var(--text-muted)" }}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          )}
        </div>
        <div style={{
          display: "flex", gap: 3,
          background: "var(--surface-1,#F1F5F9)",
          borderRadius: "var(--radius,8px)", padding: 3,
        }}>
          {[
            { key: "pending",   label: `Pending (${data.total_pending || 0})` },
            { key: "completed", label: `Completed · ${data.total_completed || 0} reports` },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: "6px 14px", borderRadius: 6,
                fontSize: 13, fontWeight: 500, cursor: "pointer",
                border: activeTab === t.key ? "0.5px solid var(--border,#E2E8F0)" : "none",
                background: activeTab === t.key ? "var(--surface-2,#fff)" : "transparent",
                color: activeTab === t.key ? "var(--text-primary)" : "var(--text-secondary)",
                transition: "all 0.15s",
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "var(--text-secondary)" }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            border: "2.5px solid var(--border,#E2E8F0)",
            borderTopColor: "var(--fill-accent,#2563EB)",
            animation: "wq-spin 0.8s linear infinite",
            margin: "0 auto 12px",
          }} />
          <style>{`@keyframes wq-spin { to { transform: rotate(360deg); } }`}</style>
          Loading queue...
        </div>
      ) : (
        <div style={{
          background: "var(--surface-2,#fff)",
          border: "0.5px solid var(--border,#E2E8F0)",
          borderRadius: 12, overflow: "hidden",
        }}>
          {/* Table header */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "2fr 1.1fr 1.3fr 1.1fr auto",
            padding: "10px 20px",
            borderBottom: "0.5px solid var(--border,#E2E8F0)",
            background: "var(--surface-1,#F8FAFC)",
            gap: 0,
          }}>
            {["Patient info", "Registered time", "Referred by", "Status", "Action"].map(h => (
              <span key={h} style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", color: "var(--text-muted)", textTransform: "uppercase" }}>
                {h}
              </span>
            ))}
          </div>

          {/* Rows */}
          {activeList.length === 0 ? (
            <div style={{ padding: "56px 20px", textAlign: "center" }}>
              {isPendingTab ? (
                <>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="1.5" aria-hidden="true" style={{ display: "block", margin: "0 auto 12px" }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "var(--text-primary)", marginBottom: 6 }}>
                    {search ? "No pending patients match your search." : "All caught up!"}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                    {search ? "Try a different search term." : "No patients are waiting for reports."}
                  </div>
                </>
              ) : (
                <>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" aria-hidden="true" style={{ display: "block", margin: "0 auto 12px" }}><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="2"/></svg>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "var(--text-primary)", marginBottom: 6 }}>
                    {search ? "No completed patients match your search." : "No completed reports yet"}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                    {search ? "Try a different search term." : `Patients with reports uploaded on ${selectedDate} will appear here.`}
                  </div>
                </>
              )}
            </div>
          ) : (
            activeList.map(p => <PatientRow key={p.id} p={p} />)
          )}
        </div>
      )}

      {/* Upload modal */}
      {uploadPatient && (
        <UploadReport
          patient={uploadPatient}
          onClose={() => setUploadPatient(null)}
          onDone={handleUploadDone}
        />
      )}
    </div>
  );
}